import React from 'react';

import { useGetDesignPagesQuery } from '../../store/services/designApi';
import { usePlanningTasks } from '../PlanningTasks';

import { ActionContinueBtn } from './ActionEntry';

export interface MissingDesignPages { count: number; label: string }

const DESIGN_STAGES = ['design_first_page', 'design_remaining'];

/** Planned pages with no design, from the server inventory, once a page is designed and nothing is generating. */
export function useMissingDesignPages(projectId?: string | null): MissingDesignPages | null {
  const response = useGetDesignPagesQuery(
    { projectId: projectId ?? '', light: true, includePlanned: true }, { skip: !projectId },
  ).currentData;
  const { tasks } = usePlanningTasks(projectId);
  const inventory = response?.page_inventory;
  // The cached page list and stage tasks are live (SSE); the inventory is refetched when readiness changes.
  const generating = response?.design_pages.some((page) => page.generation_in_progress)
    || tasks.some((task) => ['queued', 'ai_in_progress'].includes(task.status)
      && DESIGN_STAGES.includes(task.planning_metadata?.stage ?? ''));

  if (!projectId || !inventory || generating || inventory.generating || inventory.ready.length === 0
    || inventory.missing_count === 0) return null;

  return { count: inventory.missing_count, label: inventory.generate_label };
}

/** A newer gate or suggestion took the bar: it must still offer the pages that were never designed. */
export const offersPageGeneration = (labels: string[]): boolean => labels.some(
  (label) => /^(?:Generate All Pages|Generate \d+ Selected Pages?|Retry Failed Pages)\b/.test(label),
);

export const MissingPagesButton: React.FC<{ projectId: string; sessionId?: string | null; label: string }> = ({
  projectId, sessionId, label,
}) => <ActionContinueBtn phase="first_page" projectId={projectId} sessionId={sessionId} displayLabel={label}
  button={{ key: 'continue', kind: 'workflow', action: 'resume_design', label: 'Generate All Pages' }} />;
